import React from 'react';
import { Box, Text, Flex } from '@qga/roo-ui/components';
import { Flag } from './primitives';
import TextButton from 'components/TextButton';
import { ORDERED_INCLUSIONS_WHITELIST } from './constants';
import keyBy from 'lodash/keyBy';
import { createValueAddsObj } from './OfferDetails.helper';
import type { Inclusion } from 'types/property';
import useGlobalInclusions from 'hooks/optimizely/useGlobalInclusions';
import useCustomInclusions from 'hooks/optimizely/useCustomInclusions';

const INCLUSIONS_PREVIEW_AMOUNT = 3;

interface OfferInclusionsProps {
  inclusions?: Inclusion[];
  valueAdds?: string[];
  showAllInclusions?: boolean;
  onClick?: () => void;
}

const normalizeString = (str: string = '') => str.toLowerCase().trim().replace(/\s+/g, ' ');

const isDescriptionDifferent = (description: string | undefined, name: string) => {
  const normalizedName = normalizeString(name);
  const normalizedDescription = normalizeString(description);
  return description && normalizedName !== normalizedDescription;
};

const OfferInclusions = React.memo(({ inclusions = [{}], valueAdds = [], showAllInclusions = false, onClick }: OfferInclusionsProps) => {
  const { isGlobalInclusions } = useGlobalInclusions();
  const { isCustomInclusions } = useCustomInclusions();

  const processInclusions = () => {
    if (!inclusions?.length) return [];

    const inclusionsByKey = keyBy(inclusions, 'code');

    const processed = isGlobalInclusions
      ? inclusions
      : ORDERED_INCLUSIONS_WHITELIST.reduce((accum, { code, icon }) => {
          const inclusion = inclusionsByKey[code];
          if (!inclusion) return accum;
          return [...accum, { ...inclusion, icon }];
        }, []);

    return processed;
  };

  const inclusionsList = processInclusions();
  const valueAddsObj = valueAdds ? createValueAddsObj(valueAdds) : [];

  const inclusionsPlusValueAdds = [...valueAddsObj, ...inclusionsList];

  const inclusionsPlusValueAddsPreview = inclusionsPlusValueAdds.slice(0, INCLUSIONS_PREVIEW_AMOUNT);
  const inclusionsPlusValueAddsToRender = showAllInclusions ? inclusionsPlusValueAdds : inclusionsPlusValueAddsPreview;

  const inclusionsPlusValueAddsCount = inclusionsPlusValueAdds.length;
  const remainingInclusionsPlusValueAddsCount = inclusionsPlusValueAddsCount - INCLUSIONS_PREVIEW_AMOUNT;
  const showVieMoreInclusionsText = inclusionsPlusValueAddsCount > INCLUSIONS_PREVIEW_AMOUNT && !showAllInclusions;

  return (
    <Box color="greys.charcoal" mb={2}>
      <Flex flexDirection="column" data-testid="inclusions-wrapper">
        {inclusionsPlusValueAddsToRender.map(({ icon, name, description }, i) => {
          const showDescription = isDescriptionDifferent(description, name);
          const nameToDisplay = name === 'value adds' || !isCustomInclusions ? description : name;

          return (
            <Flag key={i} icon={icon}>
              <Flex flexDirection={'column'}>
                <Text fontSize="16px" fontWeight={icon === 'welcomeGift' ? 'bold' : 'normal'}>
                  {nameToDisplay}
                </Text>
                {showDescription && isCustomInclusions && (
                  <Text fontSize="14px" color="greys.steel" ml={1}>
                    {description}
                  </Text>
                )}
              </Flex>
            </Flag>
          );
        })}
      </Flex>
      {showVieMoreInclusionsText && (
        <TextButton onClick={onClick} fontSize="16px" mt="3" data-testid="view-all-inclusions-text">
          {`+ ${remainingInclusionsPlusValueAddsCount}  more ${remainingInclusionsPlusValueAddsCount === 1 ? 'inclusion' : 'inclusions'}`}
        </TextButton>
      )}
    </Box>
  );
});

export default OfferInclusions;
